/**
 * What earns a sticker (specs/006-loyalty FR-011, FR-012; research R11).
 *
 * One sticker per drink UNIT the customer paid something for. Pastries,
 * add-ons and anything charged nothing earn none, and neither does a line
 * given away — the free drink itself (FR-021).
 *
 * Pure: the caller supplies the item rules and the catalog's variant names.
 */

export type ItemKind = "drink" | "pastry" | "none";

export interface EarnLine {
  lineId: string;
  menuItemId: string;
  quantity: number;
  /** Integer centavos, after every discount. */
  lineTotal: number;
  grantedByModule: string | null;
}

/** Stickers earned, per sale line id. Lines that earned nothing are absent. */
export type StickersByLine = Record<string, number>;

const OUNCES = /\d+\s*oz\b/i;

/**
 * A manager's rule if there is one; otherwise a drink if any of the item's
 * sizes is measured in ounces (the same test as the till's cup counter), and
 * a pastry if not.
 */
export function itemKind(
  menuItemId: string,
  rules: ReadonlyMap<string, ItemKind>,
  variantNames: ReadonlyMap<string, readonly string[]>,
): ItemKind {
  const rule = rules.get(menuItemId);
  if (rule) return rule;
  return (variantNames.get(menuItemId) ?? []).some((name) => OUNCES.test(name)) ? "drink" : "pastry";
}

export function stickersByLine(
  lines: readonly EarnLine[],
  kindOf: (menuItemId: string) => ItemKind,
): StickersByLine {
  const earned: StickersByLine = {};
  for (const line of lines) {
    if (line.grantedByModule !== null) continue;
    if (line.lineTotal <= 0) continue;
    if (kindOf(line.menuItemId) !== "drink") continue;
    if (!Number.isInteger(line.quantity) || line.quantity < 1) continue;
    earned[line.lineId] = (earned[line.lineId] ?? 0) + line.quantity;
  }
  return earned;
}

export function totalStickers(byLine: StickersByLine): number {
  return Object.values(byLine).reduce((total, n) => total + n, 0);
}

/**
 * Stickers a refund takes back, per line: the refunded units of each line
 * that earned, never more than it earned less what earlier refunds already
 * took back. Positive counts; the caller signs them.
 */
export function refundReversal(
  earned: StickersByLine,
  alreadyReversed: StickersByLine,
  refunded: ReadonlyArray<{ saleLineId: string; quantity: number }>,
): StickersByLine {
  const taken: StickersByLine = {};
  for (const { saleLineId, quantity } of refunded) {
    const left = (earned[saleLineId] ?? 0) - (alreadyReversed[saleLineId] ?? 0) - (taken[saleLineId] ?? 0);
    const take = Math.min(left, quantity);
    if (take > 0) taken[saleLineId] = (taken[saleLineId] ?? 0) + take;
  }
  return taken;
}
